"use client";

import { type ReactElement, useMemo } from "react";
import { BRAND_LOGO_ALT, BRAND_LOGO_TITLE } from "../lib/brand-seo-copy";
import { cn } from "../utils";
import {
  BRAND_LOGO_TILE_RX,
  BRAND_LOGO_TRIANGLE_TIGHT_VIEWBOX,
  BRAND_LOGO_VIEWBOX_SIZE,
  BRAND_TRIANGLE_MARK_CORNER_RADIUS_PX,
  buildBrandLogoRoundedTrianglePath,
} from "./agent-mark-geometry";

export {
  BRAND_HOME_ARIA_LABEL,
  BRAND_LOGO_ALT,
  BRAND_LOGO_TITLE,
} from "../lib/brand-seo-copy";
export { BRAND_TRIANGLE_MARK_CORNER_RADIUS_PX } from "./agent-mark-geometry";

export type BrandLogoMarkVariant = "light" | "dark" | "website";

/** High-contrast disc: white triangle on dark fill (light mode), dark on white (dark mode). */
export type BrandTriangleMarkVariant = BrandLogoMarkVariant | "inverted";

export interface BrandLogoMarkProps {
  readonly width: number;
  readonly height: number;
  readonly className?: string;
  /**
   * Pin light/dark styling (e.g. login on black always uses `dark`).
   * Omit to follow the app theme via `dark:` classes.
   */
  readonly variant?: BrandLogoMarkVariant;
}

interface BrandLogoPinnedFillSpec {
  readonly tile: string;
  readonly mark: string;
}

/** Pinned fills use SVG `fill` attrs — reliable on the bookmark strip (Tailwind `fill-*` on SVG children can fail to compile). */
const PINNED_BRAND_LOGO_FILLS: Record<
  BrandLogoMarkVariant,
  BrandLogoPinnedFillSpec
> = {
  light: { tile: "#E2E5E9", mark: "#2E2E2E" },
  dark: { tile: "#2E2E2E", mark: "#FFFFFF" },
  /** White tile + black mark — `public/brand/logo-dark-website.svg`. */
  website: { tile: "#FFFFFF", mark: "#000000" },
};

function resolvePinnedBrandLogoFills(
  variant: BrandLogoMarkVariant,
): BrandLogoPinnedFillSpec {
  return PINNED_BRAND_LOGO_FILLS[variant];
}

/** Tile/mark fills aligned with `public/brand/logo-light.svg` and `logo-dark.svg`. */
function resolveBrandMarkFillClassName(mode: "tile" | "mark"): string {
  if (mode === "tile") {
    return "fill-[#E2E5E9] dark:fill-[#2E2E2E]";
  }

  return "fill-[#2E2E2E] dark:fill-white";
}

/** Full brand mark: tile + triangle (bookmark bar, login). Matches `loading-mark-agents.svg`. */
export function BrandLogoMark({
  width,
  height,
  className,
  variant,
}: BrandLogoMarkProps): ReactElement {
  const trianglePathD: string = useMemo((): string => {
    return buildBrandLogoRoundedTrianglePath(
      width,
      BRAND_TRIANGLE_MARK_CORNER_RADIUS_PX,
    );
  }, [width]);

  const pinnedFills: BrandLogoPinnedFillSpec | null =
    variant !== undefined ? resolvePinnedBrandLogoFills(variant) : null;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${BRAND_LOGO_VIEWBOX_SIZE} ${BRAND_LOGO_VIEWBOX_SIZE}`}
      width={width}
      height={height}
      overflow="hidden"
      role="img"
      aria-label={BRAND_LOGO_ALT}
      className={cn("shrink-0 object-contain", className)}
    >
      <title>{BRAND_LOGO_TITLE}</title>
      <rect
        width={BRAND_LOGO_VIEWBOX_SIZE}
        height={BRAND_LOGO_VIEWBOX_SIZE}
        rx={BRAND_LOGO_TILE_RX}
        fill={pinnedFills?.tile}
        className={
          pinnedFills === null
            ? resolveBrandMarkFillClassName("tile")
            : undefined
        }
      />
      <path
        d={trianglePathD}
        fill={pinnedFills?.mark}
        className={
          pinnedFills === null
            ? resolveBrandMarkFillClassName("mark")
            : undefined
        }
      />
    </svg>
  );
}

export type BrandColorScheme = "light" | "dark";

/** Triangle fill on high-contrast agent discs — pinned SVG attrs (Tailwind `fill-*` on paths is unreliable). */
const INVERTED_TRIANGLE_FILL: Record<BrandColorScheme, string> = {
  light: "#FFFFFF",
  dark: "#0a0a0a",
};

export interface BrandTriangleMarkProps {
  readonly width: number;
  readonly height: number;
  readonly className?: string;
  readonly variant?: BrandTriangleMarkVariant;
  /** Required with `variant="inverted"` for theme-aware pinned fills. */
  readonly colorScheme?: BrandColorScheme;
}

/**
 * Triangle-only brand mark (no tile). Same geometry/colors as {@link BrandLogoMark}.
 * For floating buttons and inline agent affordances on existing backgrounds.
 */
export function BrandTriangleMark({
  width,
  height,
  className,
  variant,
  colorScheme,
}: BrandTriangleMarkProps): ReactElement {
  const invertedFill: string | undefined =
    variant === "inverted" && colorScheme != null
      ? INVERTED_TRIANGLE_FILL[colorScheme]
      : undefined;

  const markClassName: string | undefined =
    invertedFill != null
      ? undefined
      : variant === "light"
        ? "fill-primary/60"
        : variant === "dark"
          ? "fill-white"
          : variant === "inverted"
            ? "fill-white dark:fill-neutral-950"
            : "fill-primary/60 dark:fill-white";

  const pathD: string = useMemo((): string => {
    return buildBrandLogoRoundedTrianglePath(
      width,
      BRAND_TRIANGLE_MARK_CORNER_RADIUS_PX,
    );
  }, [width]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={BRAND_LOGO_TRIANGLE_TIGHT_VIEWBOX}
      width={width}
      height={height}
      aria-hidden
      className={cn("shrink-0 object-contain", className)}
    >
      <path d={pathD} fill={invertedFill} className={markClassName} />
    </svg>
  );
}
